/**
 * Item-cost field detection and gating.
 *
 * An item's own cost — what the distributor pays before rebates — is not as
 * sensitive as net cost, but showing it beside the sell price on a line-item
 * surface discloses the margin on that line. A user deliberately denied
 * `orders:gp:view` would otherwise recover gross profit by subtraction, so
 * these fields carry their own gate.
 *
 * The action reused here is the one that already describes this exact
 * surface: `price_contracts:costs:view` — "Ability to view contract-driven
 * acquisition and sell costs on price contracts, the shop/cart, and order
 * details". Contract-driven costs are already gated by it server-side; this
 * closes the item's own cost columns behind the same action so a configured
 * column cannot route around it (PD-13666).
 *
 * Deliberately NARROW, unlike the gross-profit and net-cost detectors. Those
 * bias toward over-matching because a false negative leaks. Here a false
 * POSITIVE removes a column a user can legitimately see today, so this
 * matches an explicit set of names rather than a substring pattern.
 */

/**
 * Canonical RBAC action that gates item-cost visibility.
 * Hard-coded to avoid a cycle with `RbacActions` (this util is imported by
 * code that builds those structures).
 */
export const ITEM_COST_VIEW_PERMISSION = 'price_contracts:costs:view';

/**
 * The item cost columns, in every spelling a configured column can use:
 * the entity property name and the database column name are the same for
 * these, and `_object_field` stores them unchanged.
 *
 * `averageNetCost` is deliberately ABSENT — it is matched by the net-cost
 * detector, which gates it behind the stricter `items:net_cost:view`.
 */
const ITEM_COST_FIELD_NAMES: ReadonlySet<string> = new Set(
	['acquisitionCost', 'sellCost', 'averageCost'].map((name) =>
		name.toLowerCase(),
	),
);

/** Returns true when the given key represents an item's own cost. */
export function isItemCostFieldName(key: string): boolean {
	if (key == null || key === '') {
		return false;
	}

	return ITEM_COST_FIELD_NAMES.has(key.toLowerCase());
}
